import { default as axiosAuthenticated } from './axios-interceptor';
import axios, { AxiosRequestConfig } from 'axios';
import Cookies from 'universal-cookie';
import AuthService from '../auth/authService';
import { getLoginUrl } from 'utils/getLoginUrl';
import {
    COOKIES,
    JWT_COOKIE_KEY,
    JWT_EXPIRE_DAYS,
    JWT_HEADER,
} from '@/constants/constantsAckoDrive';
import { CookieService } from '../auth/cookie-service';

const axiosNormal = axios.create();
interface AxiosConfigWithMetadata extends AxiosRequestConfig {
    metadata?: any;
}

axiosNormal.interceptors.request.use(async function (axiosConfig: any) {
    const config: AxiosConfigWithMetadata = axiosConfig;
    if (typeof window === 'undefined') {
        config.metadata = { startTime: new Date() };
    }

    return config;
});

axiosNormal.interceptors.response.use(
    function (response: any) {
        let requestDuration;
        if (typeof window === 'undefined') {
            requestDuration =
                new Date().getTime() -
                (response.config as AxiosConfigWithMetadata)?.metadata
                    ?.startTime;
        }
        return response;
    },
    async function (error: any) {
        if (error.response) {
            try {
                const cookieService = new CookieService();
                cookieService.initializeCookies(
                    error?.config?.headers[COOKIES]
                );
                const refresh_token = cookieService.getRefreshToken();
                if (error.response.status === 401 && refresh_token) {
                    const cookies = new Cookies(
                        error?.config?.headers[COOKIES] ?? null
                    );
                    let actual_tokens = cookies.get(JWT_COOKIE_KEY) || {};
                    const resp: any =
                        await AuthService.getAccessToken(refresh_token);

                    if (!resp.success) {
                        // Refresh token is either invalid or has expired or is not present with browser. User has to login again.
                        delete error.config.headers[JWT_HEADER];
                        cookieService.setIsLoggedOut();

                        if (typeof window !== 'undefined')
                            getLoginUrl(`${window?.location?.pathname}`);

                        return await axiosNormal.request(error.config);
                    } else {
                        const reauthenticatedData = resp?.resp?.data;

                        let new_access_token =
                            reauthenticatedData['access_token'];
                        let new_refresh_token =
                            reauthenticatedData['refresh_token'];
                        cookieService.setIsReauthenticated(reauthenticatedData);
                        if (actual_tokens) {
                            actual_tokens['access'] = new_access_token;
                            actual_tokens['refresh'] = new_refresh_token;
                        }
                        // Storing the new tokens in browser for future requests
                        let expires = new Date();
                        expires.setDate(new Date().getDate() + JWT_EXPIRE_DAYS);
                        cookies.set(
                            JWT_COOKIE_KEY,
                            JSON.stringify(actual_tokens),
                            {
                                path: '/',
                                expires: expires,
                            }
                        );

                        error.config.headers[JWT_HEADER] = new_access_token;
                        error.config.baseURL = undefined;

                        // Reattempting the failed request
                        return await axiosNormal.request(error.config);
                    }
                } else if (error.response.status === 403) {
                    window.location.href = '/unauthorized';
                } else if (error.response.status === 400) {
                    return {
                        data: error.response.data,
                        is_loggedin: false,
                    };
                }
                return Promise.reject(error);
            } catch (exception) {}
        }
        return Promise.reject(error);
    }
);

export async function getRequest(
    url: any,
    useAuthentication = false,
    axiosConfig?: AxiosRequestConfig
) {
    try {
        if (axiosConfig) {
            if (useAuthentication) {
                return await axiosAuthenticated.get(url, axiosConfig);
            } else {
                return await axiosNormal.get(url, axiosConfig);
            }
        } else {
            if (useAuthentication) {
                return await axiosAuthenticated.get(url);
            } else {
                return await axiosNormal.get(url);
            }
        }
    } catch (error) {
        if (typeof window === 'undefined') {
        }
        throw error;
    }
}

export async function postRequest(
    url: any,
    payload: any,
    useAuthentication = false,
    axiosConfig?: AxiosRequestConfig
) {
    try {
        if (axiosConfig) {
            if (useAuthentication) {
                return await axiosAuthenticated.post(url, payload, axiosConfig);
            } else {
                return await axiosNormal.post(url, payload, axiosConfig);
            }
        } else {
            if (useAuthentication) {
                return await axiosAuthenticated.post(url, payload);
            } else {
                return await axiosNormal.post(url, payload);
            }
        }
    } catch (error) {
        if (typeof window === 'undefined') {
        }
        throw error;
    }
}
